/**
 * Color and lighting for the network views, as two selected themes.
 *
 * Both modes are selected rather than one being flipped from the other: fog is the reason. Aerial
 * perspective washes distant geometry toward the surface color, so a light theme's fog has to be
 * near-white and a dark theme's near-black - invert one into the other and far nodes get *brighter*
 * with distance, which reads as a light source behind the graph instead of as depth.
 *
 * `edgeInk` is a fallback now rather than what every reference wears. It used to be the rule, on
 * the argument that a host has as many reference types as component types and both wearing hues
 * would compete for the one channel a viewer decodes at a glance. That argument was about a view
 * *choosing* a second palette, and this one does not choose: a reference in Ardoq carries a color
 * and a line style, either from its type or from conditional formatting, and every other view in
 * the product draws them. Dropping them made this the only view where a dashed red dependency and
 * a solid grey one looked alike, which is a worse failure than a busy picture - and the original
 * concern is answered by the channels being different in kind, a filled disc against a
 * one-pixel line, rather than by throwing the model's own answer away.
 *
 * So: node color comes from the component, edge color and dash come from the reference, and this
 * theme supplies ink only where nothing configured either.
 */

import type { LightingModel, Rgb01 } from '../../render/lighting';
import { CATEGORICAL_FALLBACK } from '../../render/colorScale';
import { SKY_FILL_LIGHT } from '../../render/lighting';
import { toTypesByFrequency } from '../graph/typeOrder';
import type { NetworkNode } from '../types';

/** What an unparseable color becomes. Mid-grey, so a bad value is visible rather than invisible. */
const UNKNOWN_COLOR: Rgb01 = [0.5, 0.5, 0.5];

const FULL_HEX = /^#?([0-9a-f]{6})$/i;

const SHORT_HEX = /^#?([0-9a-f]{3})$/i;

const FUNCTIONAL_RGB = /^rgba?\(([^)]+)\)$/i;

/**
 * A CSS color to 0-1 channels.
 *
 * Everything on the GPU side works in 0-1 floats, and the palette is documented and validated as
 * hex - so the conversion belongs at the boundary rather than as a second copy of the palette
 * written in floats, which would be a second thing to keep in step with the validator.
 *
 * More than hex, because more than the palette reaches here. A node's color comes from a
 * component type a user configured, and the host resolves its theme tokens through
 * `getComputedStyle`, which hands back `rgb(31, 33, 38)` rather than the hex the token was written
 * as. An earlier draft parsed hex only: `Number.parseInt('rgb(31,', 16)` is `NaN`, every channel
 * came out `NaN`, and a `NaN` in a vertex buffer discards the whole triangle - so the node simply
 * was not drawn, with nothing anywhere saying why.
 *
 * Alpha is parsed and discarded. These colors are all opaque on screen: a node's transparency is
 * the emphasis channel, which the focus logic owns, and letting a type color contribute alpha
 * would make a dimmed node and a translucent type indistinguishable.
 */
export const toRgb01 = (color: string): Rgb01 => {
  const text = color.trim();

  const full = FULL_HEX.exec(text);
  if (full) {
    const value = Number.parseInt(full[1], 16);
    return [
      ((value >> 16) & 0xff) / 255,
      ((value >> 8) & 0xff) / 255,
      (value & 0xff) / 255,
    ];
  }

  const short = SHORT_HEX.exec(text);
  if (short) {
    /* `#abc` means `#aabbcc` - each digit doubled, not padded with zeroes. */
    const digits = [...short[1]].map(digit =>
      Number.parseInt(`${digit}${digit}`, 16)
    );
    return [digits[0] / 255, digits[1] / 255, digits[2] / 255];
  }

  const functional = FUNCTIONAL_RGB.exec(text);
  if (functional) {
    /* Both the legacy comma form and the modern space-separated one, since which of the two
       `getComputedStyle` returns is the browser's choice rather than the caller's. */
    const parts = functional[1]
      .split(/[\s,/]+/)
      .filter(part => part.length > 0)
      .map(Number);

    if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) {
      return [parts[0] / 255, parts[1] / 255, parts[2] / 255];
    }
  }

  return UNKNOWN_COLOR;
};

/**
 * What a type wears when the model gives it no color of its own.
 *
 * Past the fourth slot every type shares this grey. That is the rule `CATEGORICAL_FALLBACK`
 * documents rather than a shortcut: a fifth hue fails the all-pairs gate outright - brand purple
 * collides with blue at CVD delta-E 3.0 - so the tail folds into one "other" color instead of
 * inventing one. In the app this is nearly unreachable, since a component type almost always
 * carries a configured color; it is the fixture and the badly-configured workspace that land here.
 */
const OTHER_TYPE_COLOR = '#8A8A85';

export type TypeColors = {
  /** Color per type name, for the legend and as the renderer's fallback. */
  colorOf: Map<string, string>;
  /**
   * Type names that share {@link OTHER_TYPE_COLOR}, in legend order.
   *
   * Carried out separately because a legend cannot show five swatches when two of them are the
   * same grey - it has to say "3 other types" instead, and only the caller knows how to phrase it.
   */
  folded: string[];
};

/**
 * Assigns the four validated categorical slots to the four commonest types.
 *
 * `toTypesByFrequency` rather than an order of its own, so the hues that are easiest to tell apart
 * go to the types that occupy the most of the screen - and so the legend reads in the same order as
 * the bands the bundled sphere lays its types out in.
 *
 * The worst pair among the four sits in the 6-8 CVD delta-E band, which is legal only alongside a
 * second channel - so every view built on this ships a legend and a hover label, and the layered
 * layout adds plane position on top.
 */
export const toTypeColors = (nodes: readonly NetworkNode[]): TypeColors => {
  const ordered = toTypesByFrequency(nodes);

  const colorOf = new Map<string, string>();
  const folded: string[] = [];

  ordered.forEach((typeName, index) => {
    const slot = CATEGORICAL_FALLBACK[index];
    if (slot === undefined) {
      folded.push(typeName);
      colorOf.set(typeName, OTHER_TYPE_COLOR);
      return;
    }
    colorOf.set(typeName, slot);
  });

  return { colorOf, folded };
};

/**
 * A node's color: the model's own, or the categorical slot for its type.
 *
 * The model wins whenever it has an opinion, which in the app is almost always - a component type
 * carries a color and conditional formatting overrides it. The palette is the fallback, not the
 * default.
 */
export const toNodeColor = (
  { typeName, fill }: NetworkNode,
  { colorOf }: TypeColors
): string => fill ?? colorOf.get(typeName) ?? OTHER_TYPE_COLOR;

/**
 * How a reference edge combines with what is already behind it.
 *
 * `additive` treats an edge as light rather than as ink, so where a bundle runs fifty references
 * down one path the pixel is fifty times brighter and a lone reference stays a thread - which turns
 * bundle density into something a reader can see instead of a uniform grey rope. It is the reading
 * hierarchical edge bundling is drawn for.
 *
 * `alpha` on a light surface, and that is not a preference. Light accumulates toward white, and on
 * a near-white surface the edges are dark: adding them lightens each crossing toward the background
 * until the densest bundle in the estate is the one place with no edges visible at all.
 */
export type EdgeBlend = 'alpha' | 'additive';

/**
 * Screen-space ambient occlusion: a pixel darkens where a node stands just in front of it on
 * screen and close behind it in depth.
 */
export type AmbientOcclusion = {
  /** 0 draws none. */
  strength: number;
  /**
   * Whether the empty canvas around a node darkens too, as well as the nodes behind it. Off draws
   * the contact shading between nodes and nothing on the background.
   */
  halo: boolean;
};

export const DEFAULT_AMBIENT_OCCLUSION: AmbientOcclusion = {
  strength: 1,
  halo: true,
};

export const NO_AMBIENT_OCCLUSION: AmbientOcclusion = {
  strength: 0,
  halo: false,
};

export type NetworkTheme = {
  /** Cleared to this, and what the fog and the node rim resolve to. */
  surface: Rgb01;
  edgeBlend: EdgeBlend;
  /** Reference edges. */
  edgeInk: Rgb01;
  /** Hierarchy skeletons and layer grids - structure, drawn recessively. */
  guideInk: Rgb01;
  /**
   * How much of its own color a node outside the focused neighborhood keeps, 1 to 0.
   *
   * Per theme for the same reason the fog strength is: dimming mixes toward the surface color, and
   * the two surfaces are at opposite ends of the range. A sixth of the way from `#1a1a19` to a
   * saturated hue still reads as that hue in the dark; a sixth of the way from `#fcfcfb` is paper.
   */
  dimmedNodeEmphasis: number;
  ambientOcclusion: AmbientOcclusion;
  lighting: LightingModel;
  /** For the DOM chrome around the canvas, which is not the renderer's business. */
  css: {
    surface: string;
    text: string;
    mutedText: string;
    border: string;
  };
};

/**
 * The two theme properties that follow from the surface rather than from a choice.
 *
 * Derived rather than selected because the app does not know which theme it is in. It resolves a
 * design token through `getComputedStyle` and gets a color back, so "am I dark" is a question about
 * that color - and a host that shipped a third surface, or a mid-grey one, would otherwise have to
 * guess which of the two static themes to copy these from.
 *
 * Keeping the values here rather than in each theme literal also means the package's own dark and
 * light themes and the app's resolved one cannot drift apart, which is how the edge opacity ended
 * up with one value in Storybook and another in the app.
 */
export type SurfaceAdaptiveTheme = {
  edgeBlend: EdgeBlend;
  dimmedNodeEmphasis: number;
  /**
   * How much of the far edge washes toward the surface color.
   *
   * Lower on a light surface than the encoded arithmetic wanted. The renderer mixes fog in linear
   * radiance now, and mixing toward near-white in linear lifts a midtone faster than mixing the
   * encoded values did - at the strength that read as a faint haze before, the whole estate came
   * out pastel. Toward near-black the effect runs the other way and is far smaller, so the dark
   * strength is the measured one it always was.
   */
  fogStrength: number;
  ambientOcclusion: AmbientOcclusion;
};

/** Rec. 709 luma, which is what "dark" means to an eye rather than to a mean of three channels. */
const toRelativeLuminance = ([red, green, blue]: Rgb01): number =>
  0.2126 * red + 0.7152 * green + 0.0722 * blue;

const isDarkSurface = (surface: Rgb01): boolean =>
  toRelativeLuminance(surface) < 0.5;

export const toSurfaceAdaptiveTheme = (surface: Rgb01): SurfaceAdaptiveTheme =>
  isDarkSurface(surface)
    ? {
        edgeBlend: 'additive',
        dimmedNodeEmphasis: 0.16,
        fogStrength: 0.3,
        ambientOcclusion: DEFAULT_AMBIENT_OCCLUSION,
      }
    : /* Twice the dark floor, because it is measured against the opposite end of the range: the
         same fraction of the way toward `#fcfcfb` leaves a node indistinguishable from paper. */
      {
        edgeBlend: 'alpha',
        dimmedNodeEmphasis: 0.32,
        fogStrength: 0.17,
        ambientOcclusion: DEFAULT_AMBIENT_OCCLUSION,
      };

/**
 * A key light that follows the camera.
 *
 * Fixed to the view rather than to the world, which for a sphere is the only choice that works: a
 * world light leaves half the nodes in shadow at every angle, and a shadowed node reads as a
 * darker category rather than as a node facing away. `SKY_FILL_LIGHT` is reused unchanged as the
 * second light, since a cool overhead fill does the same job here as on a surface.
 */
const NETWORK_KEY_LIGHT = {
  direction: [-0.25, 0.3, 0.92] as const,
  space: 'view' as const,
  color: [1, 1, 1] as const,
  diffuse: 0.34,
  specular: 0.22,
};

/**
 * Brighter and glossier than the surface rig.
 *
 * A surface is judged by its color, so lighting there is held to unity to keep the ramp honest. A
 * sphere is judged by its shape, and shape is all the lighting: a matte sphere under a unity rig is
 * a flat disc, which loses the one cue that separates a node in front from a node behind.
 */
const toNetworkLighting = (
  fogColor: Rgb01,
  fogStrength: number
): LightingModel => ({
  ambient: 0.52,
  shininess: 20,
  lights: [SKY_FILL_LIGHT, NETWORK_KEY_LIGHT],
  fog: { color: fogColor, strength: fogStrength },
});

/** `#1a1a19`, the documented dark chart surface. */
const DARK_SURFACE = toRgb01('#1A1A19');

/** `#fcfcfb`, the documented light chart surface. */
const LIGHT_SURFACE = toRgb01('#FCFCFB');

export const DARK_NETWORK_THEME: NetworkTheme = {
  surface: DARK_SURFACE,
  ...toSurfaceAdaptiveTheme(DARK_SURFACE),
  edgeInk: toRgb01('#9C9C96'),
  guideInk: toRgb01('#3A3A36'),
  /* Measured rather than chosen. At 0.55 the depth cue was unmistakable and the view was also
     unreadable: fog is applied at every depth, not only at the back, so a node at the center of
     the cube was already 28% of the way to the surface color and the whole graph sat a few
     percent off black. 0.3 is the most that leaves a far node still legibly its own color. */
  lighting: toNetworkLighting(
    DARK_SURFACE,
    toSurfaceAdaptiveTheme(DARK_SURFACE).fogStrength
  ),
  css: {
    surface: '#1A1A19',
    text: '#F5F5F0',
    mutedText: '#A3A39C',
    border: '#383835',
  },
};

export const LIGHT_NETWORK_THEME: NetworkTheme = {
  surface: LIGHT_SURFACE,
  ...toSurfaceAdaptiveTheme(LIGHT_SURFACE),
  edgeInk: toRgb01('#6B6B66'),
  guideInk: toRgb01('#D8D8D2'),
  lighting: toNetworkLighting(
    LIGHT_SURFACE,
    toSurfaceAdaptiveTheme(LIGHT_SURFACE).fogStrength
  ),
  css: {
    surface: '#FCFCFB',
    text: '#1A1A19',
    mutedText: '#6B6B66',
    border: '#D8D8D2',
  },
};
